import { AppState, Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import {
    Route,
    BrowserRouter as Router,
    Routes,
    useNavigate,
} from 'react-router-dom'
import LoadingSpinner from './components/LoadingSpinner'
import { useUserDataStore } from './stateStores/User'
import tenantApi from './api/tenant/TenantApi'
import { initializeAuth0Instance } from './api/auth0Helper'
import Login2 from './pages/Login2'
import { Pages } from './Pages'
import Logout from './pages/Logout'
import Roles from './pages/Roles'
import CreateRole from './pages/CreateRole'
import CsvImport from './pages/CsvImport'
import Tenants from './pages/Tenants'
import ManageTenant from './pages/ManageTenant'
import CreateTenant from './pages/CreateTenant'
import CreateUser from './pages/CreateUser'
import UpdateUser from './pages/UpdateUser'
import { UserRoles } from './api/tenant/models/UserDto'
import { AuthHelper } from './auth/AuthHelper'
import AccessDenied from './pages/AccessDenied'
import Note from './components/Note'
import { NoteType } from './enums/NoteType'
import BlankLayout from './components/BlankLayout'
import NotFound404 from './pages/NotFound404'
import UpdateRole from './pages/UpdateRole'
import DataManagement from './pages/DataManagement'
import ManageRole from './pages/ManageRole'
import { ManageRoleProvider } from './components/manageRole/stateStore/ManageRoleProvider'
import CandidateSearchVectorTests from './pages/CandidateSearchVectorTests'
import HelixSearchPageWrapper from './components/helixSearch/HelixSearchPageWrapper'

function App(): React.JSX.Element {
    return (
        <Router>
            <Auth0Wrapper>
                <AuthInitializer>
                    <Routes>
                        {/* public routes */}
                        <Route path={Pages.Login} element={<Login2 />} />
                        <Route
                            path={Pages.Auth0Callback}
                            element={<Auth0CallbackPage />}
                        />
                        <Route path={Pages.Logout} element={<Logout />} />
                        <Route
                            path={Pages.AccessDenied}
                            element={<AccessDenied />}
                        />
                        {/* private routes */}
                        <Route
                            path={Pages.Roles}
                            element={
                                <PrivateRoute
                                    allowedRoles={[UserRoles.TA, UserRoles.HM]}
                                >
                                    <Roles />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path={Pages.CreateNewRole}
                            element={
                                <PrivateRoute allowedRoles={[UserRoles.TA]}>
                                    <CreateRole />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path={Pages.UpdateRole}
                            element={
                                <PrivateRoute allowedRoles={[UserRoles.TA]}>
                                    <UpdateRole />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path={Pages.ImportLinkedHelperCsv}
                            element={
                                <PrivateRoute allowedRoles={[UserRoles.ADMIN]}>
                                    <CsvImport />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path={Pages.Tenants}
                            element={
                                <PrivateRoute allowedRoles={[UserRoles.ADMIN]}>
                                    <Tenants />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path={Pages.Tenant}
                            element={
                                <PrivateRoute allowedRoles={[UserRoles.ADMIN]}>
                                    <ManageTenant />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path={Pages.CreateNewTenant}
                            element={
                                <PrivateRoute allowedRoles={[UserRoles.ADMIN]}>
                                    <CreateTenant />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path={Pages.CreateNewUser}
                            element={
                                <PrivateRoute allowedRoles={[UserRoles.ADMIN]}>
                                    <CreateUser />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path={Pages.UpdateUser}
                            element={
                                <PrivateRoute allowedRoles={[UserRoles.ADMIN]}>
                                    <UpdateUser />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path={Pages.ManageRole}
                            element={
                                <PrivateRoute
                                    allowedRoles={[UserRoles.TA, UserRoles.HM]}
                                >
                                    <ManageRoleProvider>
                                        <ManageRole />
                                    </ManageRoleProvider>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path={Pages.DataManagement}
                            element={
                                <PrivateRoute allowedRoles={[UserRoles.ADMIN]}>
                                    <DataManagement />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path={Pages.CandidateSearchVectorTests}
                            element={
                                <PrivateRoute allowedRoles={[UserRoles.ADMIN]}>
                                    <CandidateSearchVectorTests />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path={Pages.HelixSearch}
                            element={
                                <PrivateRoute allowedRoles={[UserRoles.ADMIN]}>
                                    <HelixSearchPageWrapper />
                                </PrivateRoute>
                            }
                        />
                        <Route path="*" element={<NotFound404 />} />
                    </Routes>
                </AuthInitializer>
            </Auth0Wrapper>
        </Router>
    )
}

function AuthInitializer({ children }: { children: React.ReactNode }) {
    const [setup, setSetup] = useState(false)
    const auth0 = useAuth0()

    useEffect(() => {
        if (setup) {
            // we've already been set up, no need to do it again
            return
        }
        initializeAuth0Instance(auth0)
        setSetup(true)
    }, [auth0, setup])

    if (setup) {
        return <>{children}</>
    } else {
        return <></>
    }
}

function PrivateRoute({
    children,
    allowedRoles,
}: {
    children: React.ReactNode
    allowedRoles: UserRoles[]
}): React.JSX.Element {
    const { isAuthenticated, isLoading, error, user, loginWithRedirect } =
        useAuth0()
    const { userData, setUserData, clearUserData } = useUserDataStore()
    const navigate = useNavigate()
    const [getUserErrorStr, setGetUserErrorStr] = useState<string | undefined>()

    useEffect(() => {
        if (!error && !isLoading && !isAuthenticated) {
            // we're trying to access a private route but we're not authenticated - redirect to the login page
            loginWithRedirect({
                appState: {
                    returnTo: window.location.pathname + window.location.search,
                },
            })
            return
        }

        const fetchUserData = async () => {
            try {
                if (!user || !isAuthenticated || isLoading || error) {
                    return
                }

                // reset any previous error message
                setGetUserErrorStr(undefined)

                const getLoggedInUserRes = await tenantApi.getLoggedInUser()

                setUserData({
                    user: {
                        ...getLoggedInUserRes.user,
                        auth0PicUrl: user.picture,
                    },
                    tenant: getLoggedInUserRes.tenant,
                })
            } catch (error) {
                console.error('Error fetching user:', error)
                clearUserData()
                if (error instanceof Error) {
                    if (
                        error.message.includes(
                            'Unknown or invalid refresh token.',
                        )
                    ) {
                        // user is logged out - redirect to the login page
                        loginWithRedirect({
                            appState: {
                                returnTo:
                                    window.location.pathname +
                                    window.location.search,
                            },
                        })
                        return
                    }
                    setGetUserErrorStr(error.message)
                } else {
                    setGetUserErrorStr(JSON.stringify(error, null, 4))
                }
            }
        }

        if (isAuthenticated && !isLoading && !error && user) {
            if (!userData) {
                // no db user stored locally - go and get it
                fetchUserData()
            } else if (userData.user.auth0UserId !== user.sub) {
                // the user in the db doesn't match the authenticated user - go and get it
                clearUserData()
                fetchUserData()
            }
        }

        // if we're fully loaded and authenticated, check roles
        if (
            isAuthenticated &&
            !isLoading &&
            !error &&
            user &&
            userData &&
            userData.user.auth0UserId === user.sub
        ) {
            if (!AuthHelper.userHasSomeRoles(userData.user, allowedRoles)) {
                navigate(Pages.AccessDenied)
            }
        }
    }, [
        isAuthenticated,
        isLoading,
        error,
        loginWithRedirect,
        user,
        userData,
        setUserData,
        clearUserData,
        allowedRoles,
        navigate,
    ])

    if (error) {
        return (
            <BlankLayout>
                <Note type={NoteType.ERROR}>
                    There was an error authenticating the user. Please refresh
                    to try again. Error details: {getUserErrorStr}
                </Note>
            </BlankLayout>
        )
    }

    if (isLoading) {
        return <LoadingSpinner />
    }

    if (getUserErrorStr) {
        return (
            <BlankLayout>
                <Note type={NoteType.ERROR}>
                    There was an error getting the user from the database.
                    Please refresh to try again. Error details:{' '}
                    {getUserErrorStr}
                </Note>
            </BlankLayout>
        )
    }

    // there's no error and Auth0 isn't loading any more - are we authenticated AND we've got the user from the DB
    //  AND the cached DB user is the same as the authenticated auth0 user?
    if (
        isAuthenticated &&
        user &&
        userData &&
        userData.user.auth0UserId === user.sub
    ) {
        return <>{children}</>
    }

    // we are about to be redirected to the Auth0 login page
    return <LoadingSpinner />
}

function Auth0CallbackPage(): React.JSX.Element {
    const { error, logout } = useAuth0()

    if (error) {
        return (
            <BlankLayout>
                <div>
                    <Note type={NoteType.ERROR} containerClassNames="bg-white">
                        There was a problem authenticating: {error.message}
                    </Note>
                </div>
                <div>
                    <button
                        className="flex items-center w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
                        onClick={() =>
                            logout({
                                logoutParams: {
                                    returnTo: window.location.origin,
                                },
                            })
                        }
                    >
                        Try Again
                    </button>
                </div>
            </BlankLayout>
        )
    }

    // this is only shown when Auth0 is doing its thing and we are waiting for onRedirectCallback to be triggered
    return <LoadingSpinner />
}

function Auth0Wrapper({
    children,
}: {
    children: React.ReactNode
}): React.JSX.Element {
    const navigate = useNavigate()

    const onRedirectCallback = (appState?: AppState) => {
        const navValue = appState?.returnTo || Pages.Roles
        navigate(navValue)
    }

    return (
        <Auth0Provider
            domain={import.meta.env.VITE_AUTH0_DOMAIN}
            clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
            authorizationParams={{
                redirect_uri: `${window.location.origin}${Pages.Auth0Callback}`,
                audience: import.meta.env.VITE_AUTH0_AUDIENCE,
                scope: 'openid profile email offline_access',
            }}
            onRedirectCallback={onRedirectCallback}
            cacheLocation="localstorage"
            useRefreshTokens={true}
        >
            {children}
        </Auth0Provider>
    )
}

export default App
